import { channelCommand, activeChannelName } from './chatChannel'

export const isUndefined = (obj) => obj === undefined;
export const isNull = (obj) => obj === null;
export const isNullOrUndefined = (obj) => isNull(obj) || isUndefined(obj);
export const isNotNullOrUndefined = (obj) => !isNullOrUndefined(obj);
export const setWithDefault = (value, _default) => isUndefined(value) || isNull(value) || isBlank(value) ? _default : value;
export const isPresent = (obj) => {
  // Handle null/undefined
  if (obj == null) {
    return false
  }

  // Handle strings (including empty or whitespace-only strings)
  if (typeof obj === 'string') {
    return obj.trim().length > 0;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.length > 0;
  }

  // Handle objects
  if (typeof obj === 'object') {
    return Object.keys(obj).length > 0;
  }

  // Handle other types (numbers, booleans, etc.)
  return true;
};
export const isBlank = (obj) => !isPresent(obj);

// Grab the CSRF token from the meta tag
export const csrfToken = () => {
  const csrfTokenTag = document.querySelector("[name='csrf-token']");
  return isNullOrUndefined(csrfTokenTag) ? null : csrfTokenTag.content;
}

export const capitalize = (word) => {
  return `${word.slice(0, 1).toUpperCase()}${word.slice(1).toLowerCase()}`;
}

export const camelize = (text, separator = '_') => {
  const words = text.split(separator)
  const result = [capitalize(words[0])]
  words.slice(1).forEach((word) => result.push(capitalize(word)))
  return result.join('')
}

export const randomInteger = () => Math.floor(Math.random() * 10000);


export const fetchStream = async (url, successCallback, options, body, errorCallback, catchCallback) => {
  if (isNullOrUndefined(options)) {
    options = {
      method: 'GET'
    };
  }

  if (isNullOrUndefined(options.method)) {
    options.method = 'GET'
  }

  if (isNullOrUndefined(options.headers)) {
    options.headers = {
      "X-CSRF-Token": csrfToken()
    }
  }

  if (isNullOrUndefined(options.headers['X-CSRF-Token'])) {
    options.headers["X-CSRF-Token"] = csrfToken();
  }

  if (options.headers['X-CSRF-Token'] === false) {
    delete options.headers["X-CSRF-Token"];
  }

  if (isNotNullOrUndefined(body)) {
    if (body instanceof FormData || typeof (body) === 'string') {
      options.body = body;
    } else if (body instanceof Object) {
      options.body = JSON.stringify(body)
      options.headers['Content-Type'] = 'application/json';
    }
  }

  if (isNullOrUndefined(catchCallback)) {
    catchCallback = (error) => {
      errorMessage({ controller: 'utilities', action: "fetchStreamDefaultCatchCallback", error: error });
    }
  }

  if (isNullOrUndefined(errorCallback)) {
    errorCallback = (response) => {
      errorMessage({ controller: 'utilities', action: "fetchStreamDefaultErrorCallback", response: response });
    }
  }

  const response = await fetch(url, options).catch(catchCallback);
  const data = await response.text();
  const contentType = (await response).headers.get('Content-Type');
  const jsonMatcher = new RegExp('application/json');
  const xmlMatcher = new RegExp('application/xml');
  let parsedData;

  if (jsonMatcher.test(contentType)) {
    parsedData = JSON.parse(data);
  } else if (xmlMatcher.test(contentType)) {
    const parser = new DOMParser();

    parsedData = parser.parseFromString(data, "application/xml");
  } else {
    parsedData = data;
  }

  if (response.status >= 200 && response.status < 300) {
    successCallback(parsedData)
  } else {
    errorCallback(parsedData);
  }
}

export const formattedSize = (size) => {
  if (size < 1024) {
    return `${size} octets`;
  }
  if (size < 1024 * 1024) {
    return `${Math.ceil(size / 1024)} Ko`;
  }
  if (size < 1024 * 1024 * 1024) {
    return `${Math.ceil(size / 1024 / 1024)} Mo`;
  }
  if (size < 1024 * 1024 * 1024 * 1024) {
    return `${Math.ceil(size / 1024 / 1024 / 1024)} Go`;
  }
}

export const executeIfAllowed = (callback) => {
  grecaptcha.ready(function () {
    grecaptcha.execute(window.recaptcha_site_key, { action: 'action' }).then(function (token) {
      debugMessage("Checking captcha");

      fetch(`/partials/check_allowed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          "X-CSRF-Token": csrfToken()
        },
        body: JSON.stringify({ recaptcha_token: token })
      }).then((response) => {
        debugMessage(`Captcha checked: status = ${response.status}`);

        if (response.status >= 200 && response.status < 300) {
          callback.call(response);
        }
      }).catch((error) => {
        errorMessage("Captcha failed with error:", error);
        // Noop
      });
    })
  })
}

export const validationAlert = async (message) => {
  return new Promise((resolve) => {
    const confirmation = window.confirm(message);
    if (confirmation) {
      resolve(true);
    }
  });
};

export const dateTimeString = (date) => {
  if (isNullOrUndefined(date)) {
    date = new Date();
  }

  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0'); // January is 0!
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const MM = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');

  return `${dd}/${mm}/${yyyy} ${hh}:${MM}:${ss}`;
}

export const UUIDv4 = new function () {
  function generateNumber(limit) {
    const value = limit * Math.random();
    return value | 0;
  }

  function generateX() {
    const value = generateNumber(16);
    return value.toString(16);
  }

  function generateXes(count) {
    let result = '';
    for (let i = 0; i < count; ++i) {
      result += generateX();
    }
    return result;
  }

  function generateVariant() {
    const value = generateNumber(16);
    const variant = (value & 0x3) | 0x8;
    return variant.toString(16);
  }

  // UUID v4
  //
  //   varsion: M=4
  //   variant: N
  //   pattern: xxxxxxxx-xxxx-Mxxx-Nxxx-xxxxxxxxxxxx
  //
  this.generate = function () {
    return generateXes(8)
      + '-' + generateXes(4)
      + '-' + '4' + generateXes(3)
      + '-' + generateVariant() + generateXes(3)
      + '-' + generateXes(12)
  };
};

export const displayAlert = (message, alertType) => {
  const allowedAlertTypes = ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark'];
  let validAlertType = allowedAlertTypes.find((aT) => aT === alertType);

  if (isNullOrUndefined(validAlertType)) {
    validAlertType = 'light';
  }

  document.getElementById('errors').innerHTML = `
      <div data-controller="flashes" class="alert alert-${validAlertType} alert-dismissible fade show my-0" role="alert">
        ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>
    `;
}

export const executeScript = (url) => {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.defer = true;
  script.src = url;
  document.head.appendChild(script);
}

export const googleMapFrontApiKey = 'AIzaSyBJ-6gezVBmUeJDu5COZW0lQZYbwVRzD0o';

export const loadGoogleMapFrontApi = () => {
  const key = googleMapFrontApiKey;
  if (isNullOrUndefined(window.googleFrontApiLoaded)) {
    (g => {
      let h, a, k, p = "The Google Maps JavaScript API", c = "google", l = "importLibrary", q = "__ib__",
        m = document, b = window;
      b = b[c] || (b[c] = {});
      let d = b.maps || (b.maps = {}), r = new Set, e = new URLSearchParams,
        u = () => h || (h = new Promise(async (f, n) => {
          await (a = m.createElement("script"));
          e.set("libraries", [...r] + "");
          for (k in g) e.set(k.replace(/[A-Z]/g, t => "_" + t[0].toLowerCase()), g[k]);
          e.set("callback", c + ".maps." + q);
          a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
          d[q] = f;
          a.onerror = () => h = n(Error(p + " could not load."));
          a.nonce = document.querySelector('meta[name="csp-nonce"]').content
          m.head.append(a)
        }));
      d[l] ? console.warn(p + " only loads once. Ignoring:", g) : d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n))
    })({
      key: key,
      v: "weekly"
    });
    window.googleFrontApiLoaded = true
  }
}

export const htmlNodeFromText = (text) => {
  const template = document.createElement('template');
  template.innerHTML = text;
  const node = template.content.cloneNode(true);
  return node.children[0];
}

export const debugMessage = (message, key) => {
  if (isNullOrUndefined(key)) {
    key = 'maydayDebugJS'
  }

  if (window.localStorage.getItem(key) === 'true') {
    console.log(message)
  }
}

export const errorMessage = (...data) => {
  console.error(...data)
}

export const isIosBrowser = () => {
  const ua = navigator.userAgent;
  return !window.MSStream && !/android/i.test(ua) && /iPad|iPhone|iPod/.test(ua) && /WebKit/.test(ua);
}

export const sendAcCommand = (cable, actionName, args = {}) => {
  if (isNullOrUndefined(cable) || isNullOrUndefined(args)) {
    return;
  }

  // The connected server may still run the legacy channel, which spells some
  // commands differently and does not implement others at all.
  const command = channelCommand(actionName);

  if (isNull(command)) {
    debugMessage(
      `[BeneficiaryChannel]${actionName} Skipped, unsupported by ${activeChannelName()}`,
      'beneficiaryChannel'
    );

    return;
  }

  args.action = command;

  debugMessage(
    `[BeneficiaryChannel]${command} Send ${JSON.stringify(args)}`,
    'beneficiaryChannel'
  );

  cable.send(args);
}

// Check if a value is a valid French license plate format
// Valid formats:
//   - New: 2 uppercase letters + 3 digits + 2 uppercase letters (e.g., GG489SZ)
//   - Old: 2 uppercase letters + dash + 3 digits + dash + 2 uppercase letters (e.g., GG-489-SZ)
export const isValidFrenchPlate = (plate) => {
  // Handle null, undefined, empty strings, whitespace
  if (!isPresent(plate)) {
    return false;
  }

  // Trim and check if it matches either French plate pattern
  const trimmedPlate = String(plate).trim();
  const newFormatPattern = /^[A-Z]{2}\d{3}[A-Z]{2}$/;
  const oldFormatPattern = /^[A-Z]{2}-\d{3}-[A-Z]{2}$/;

  return newFormatPattern.test(trimmedPlate) || oldFormatPattern.test(trimmedPlate);
};
